import React, { useState, useEffect, useRef } from 'react';
import { Button, Avatar, Popover } from '@barges/react';

import '@barges/scss/lib/avatar.css';
import '@barges/scss/lib/popover.css';
import { TFunction } from 'i18next';
import { SvgIcon, PurchaseSvgIcon } from '@common/sprites';
import cn from 'classnames';
import { getAdvisorBonusApi } from '@app/config/api';
import { Trans } from 'react-i18next';
import { getPurchaseSpriteUrl, loadSVGSprite } from '@common/sprites/loadSprite';
import { isKA } from '@common/config/utils';
import classes from './classes.module.scss';
import withPGTranslation from '../../config/withPGTranslation';
import PurchaseButton from './purchase_button';
import { checkIfCanMakeApplePayPayments } from '../../actions/purchase_details';

import { advisorTipsBonusesSubmitApi } from '../../config/api';
import { parseAndTrackAnalyticEvents } from '../../actions/analytics';

export interface ParamsProps {
  bonus_amount: Partial<Advisor.Bonus>;
  payment_amount?: {
    amount: number,
    kind: Payment.KindType,
    id: number,
    gatewayName: string,
    context: object,
    purchaseFees: number
  }
}

export interface MainAdvisorBonusProps {
  t: TFunction;
  user: User.Details;
  loading: boolean;
  skipBtnHandler: (params?: object) => void;
  initialLoadedHandler: () => void;
  forMobileSendBonusBtnOnClickHandler?: (params: ParamsProps) => void;
  orderId?: number;
  classNames?: string;
}

const bonusIconsIds = ['low_bonus', 'medium_bonus', 'high_bonus'];

const AdvisorBonus = (props: MainAdvisorBonusProps) => {
  const {
    t, user, loading, skipBtnHandler, initialLoadedHandler, forMobileSendBonusBtnOnClickHandler, orderId, classNames
  } = props;
  const currentRef = useRef(null);
  const [selectedBonus, setSelectedBonus] = useState<Partial<Advisor.Bonus>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fetchData, setFetchData] = useState(null);
  const [purchaseButtonProcessing, setPurchaseButtonProcessing] = useState(false);
  const advisor = fetchData?.advisor;
  const bonuses: Advisor.Bonus[] = fetchData?.advisorBonus?.bonusAmounts || [];
  const skeletonLoading = loading || isLoading;
  const userCredit = parseFloat(user?.clientAvailableCredit || '0');

  useEffect(() => {
    loadSVGSprite(getPurchaseSpriteUrl(), 'purchase-svg-sprite-container');
  }, []);

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [currentRef.current]);

  useEffect(() => {
    if (orderId) {
      setIsLoading(true);
      (async () => {
        await getAdvisorBonusApi(orderId).then((data) => {
          const defaultBonus = data?.advisorBonus?.bonusAmounts?.find((bonus: Advisor.Bonus) => bonus.default);
          setSelectedBonus(defaultBonus);
          setFetchData(data);
          setIsLoading(false);
          checkIfCanMakeApplePayPayments().then(() => {
            if (initialLoadedHandler) initialLoadedHandler();
          });
        });
      })();
    }
  }, [orderId]);

  const renderAdvisorAvatar = () => (
    <div className={ classes.advisorAvatarContainer }>
      <Avatar
        imgSrc={ fetchData?.advisor?.profilePictureUrl }
        size="large"
        type={ isKA() ? 'rectangle' : 'round' }
      />
    </div>
  );

  const getBalanceKey = () => {
    if (userCredit === 0) {
      return 'advisor_bonus.no_balance';
    }
    return `advisor_bonus.${ userCredit >= selectedBonus?.amount ? 'enough_balance' : 'partial_balance' }`;
  };

  const renderBalanceContainer = () => (
    <div className={ cn(classes.balanceContainer, { [classes.skeleton]: skeletonLoading }) } data-testid="advisor-balance-container">
      <div className={ classes.balanceValue }>
        { t('advisor_bonus.your_balance', { amount: user?.clientAvailableCredit }) }
      </div>
      <div className={ classes.balanceText }>
        <Trans
          i18nKey={ getBalanceKey() }
        />
      </div>
    </div>
  );

  const renderBonusList = () => (
    <div className={ cn(classes.bonusListContainer, { [classes.skeleton]: skeletonLoading }) } data-testid="advisor-bonus-list">
      { bonuses.map((bonus, index) => (
        <div
          key={ `amount-${ bonus.amount }` }
          className={ cn(classes.bonusItem, { [classes.active]: bonus.amount === selectedBonus.amount }) }
          onClick={ () => setSelectedBonus(bonus) }
          onKeyDown={ () => setSelectedBonus(bonus) }
          data-testid={ `advisor-bonus-item-${ bonus.amount  }` }
        >
          <SvgIcon
            id={ bonusIconsIds[index] }
            className={ classes.bonusIcon }
            size={ 75 }
          />
          <div className={ classes.bonusAmount }>
            $
            { bonus.amount }
          </div>
        </div>
      ))}
    </div>
  );

  const renderAdvisorTitle = () => (
    <div className={ cn(classes.advisorTitleContainer, { [classes.skeleton]: skeletonLoading }) } data-testid="advisor-bonus-title-container">
      <div className={ classes.advisorTitleText }>
        { t('advisor_bonus.title', { name: advisor?.nickname }) }
      </div>
      <div className={ classes.subtitleText }>
        { t('advisor_bonus.subtitle') }
      </div>
    </div>
  );

  const onCancel = () => {
    if (skipBtnHandler) {
      skipBtnHandler();
    }
  };

  const onSkipClick = () => {
    let analyticProperties = {};
    if (fetchData?.skipEventProperties) {
      const { name, properties } = fetchData.skipEventProperties;
      analyticProperties = { [name]: { ...properties } };
    }
    if (forMobileSendBonusBtnOnClickHandler) {
      skipBtnHandler({ analytic_events: analyticProperties });
    } else {
      parseAndTrackAnalyticEvents(analyticProperties);
      onCancel();
    }
  };

  const [bonusSentPopoverShow, setBonusSentPopoverShow] = useState(false);

  const bonusSentPopoverOnSuccessClick = () => {
    setBonusSentPopoverShow(false);
    onCancel();
  };

  const renderBonusSentPopover = () => (
    <Popover
      isOpen={ bonusSentPopoverShow }
      renderTitle={ () => <></> }
      key="bonus-sent-popover"
      width="343px"
      centered
      style={ {
        padding: 25,
        position: 'fixed'
      } }
    >
      <div className={ classes.bonusSentPopoverContainer }>
        <div className={ classes.bonusSentPopoverIconContainer }>
          <SvgIcon
            id="advisor_bonus_sent_icon"
            className={ classes.bonusSentPopoverIcon }
            size={ 40 }
          />
        </div>
        <div className={ classes.bonusSentTextContainer }>
          <div className={ classes.bonusSentTitle }>
            {t('advisor_bonus.bonus_sent')}
          </div>
          <div className={ classes.bonusSentText }>
            <Trans
              i18nKey="advisor_bonus.your_bonus_is_on_its_way"
            />
          </div>
        </div>
        <div className={ classes.bonusSentPopoverOkButtonContainer }>
          <button
            type="button"
            className={ classes.bonusSentPopoverOkButton }
            onClick={ bonusSentPopoverOnSuccessClick }
          >
            { t('general.ok') }
          </button>
        </div>
      </div>
    </Popover>
  );

  const [bonusSentPopoverErroShow, setBonusSentPopoverErrorShow] = useState<{ error?: string, description?: string }>({});

  const bonusSentPopoverErrorOnOkClick = () => {
    setBonusSentPopoverErrorShow({});
    onCancel();
  };

  const renderBonusSentPopoverError = () => (
    <Popover
      isOpen={ Object.keys(bonusSentPopoverErroShow).length > 0 }
      renderTitle={ () => <></> }
      width="343px"
      key="bonus-sent-popover-error"
      centered
      style={ {
        padding: 25,
        position: 'fixed'
      } }
    >
      <div className={ classes.bonusSentPopoverErrorContainer }>
        <div className={ classes.bonusSentPopoverErrorIconContainer }>
          <SvgIcon
            id="advisor_bonus_error_icon"
            className={ classes.bonusSentPopoverErrorIcon }
          />
        </div>
        <div className={ classes.bonusSentTextContainer }>
          <div className={ classes.bonusSentTitle }>
            { bonusSentPopoverErroShow?.error }
          </div>
          <div className={ classes.bonusSentText }>
            { bonusSentPopoverErroShow?.description }
          </div>
        </div>
        <div className={ classes.bonusSentPopoverOkButtonContainer }>
          <button
            type="button"
            className={ classes.bonusSentPopoverOkButton }
            onClick={ bonusSentPopoverErrorOnOkClick }
          >
            { t('general.ok') }
          </button>
        </div>
      </div>
    </Popover>
  );

  const forMobileBuyBtnOnClick = (requiredCredit?: number) => {
    if (!forMobileSendBonusBtnOnClickHandler) return;
    setPurchaseButtonProcessing(true);

    let paymentAmountParams = {};
    if (requiredCredit) {
      const currentPaymentOption = fetchData.currentPaymentOption[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { purchaseDetails } = (window as any).store.getState();
      paymentAmountParams = {
        payment_amount: {
          amount: requiredCredit,
          kind: currentPaymentOption.kind,
          id: currentPaymentOption.id,
          gatewayName: currentPaymentOption.gatewayName,
          context: currentPaymentOption.context,
          purchaseFees: purchaseDetails?.fees?.purchaseFees
        }
      };
    }

    const params: ParamsProps = {
      bonus_amount: {
        ...selectedBonus
      },
      ...paymentAmountParams
    };

    forMobileSendBonusBtnOnClickHandler(params);
    setPurchaseButtonProcessing(false);
  };

  const getforMobileBuyBtnOnClick = () => {
    if (forMobileSendBonusBtnOnClickHandler) {
      return forMobileBuyBtnOnClick;
    }
    return null;
  };

  const onSubmitAction = () => {
    setPurchaseButtonProcessing(true);
    if (forMobileSendBonusBtnOnClickHandler) {
      forMobileBuyBtnOnClick();
      setPurchaseButtonProcessing(false);
      return;
    }
    const params = {
      bonus: {
        price: selectedBonus.amount,
        kind: selectedBonus.kind
      }
    };

    advisorTipsBonusesSubmitApi({ orderId, params }).then((res: { analyticEvents: Analytic.Event }) => {
      const { analyticEvents } = res;
      setBonusSentPopoverShow(true);
      parseAndTrackAnalyticEvents(analyticEvents);
      setPurchaseButtonProcessing(false);
    }).catch((error) => {
      error.json().then((jsonError: object) => {
        setBonusSentPopoverErrorShow(jsonError);
        setPurchaseButtonProcessing(false);
      });
    });
  };

  const getPurchaseButtonTitle = () => {
    if (userCredit >= selectedBonus?.amount) {
      return t('advisor_bonus.send_bonus');
    }
    switch (fetchData?.currentPaymentOption[0]?.kind) {
      case 'cc':
      case 'am':
        return t('advisor_bonus.send_bonus');
      case 'pp':
        return (
          <>
            <PurchaseSvgIcon
              id="purchase_button_paypal_icon"
              className={ classes.advisorBonusSourceImg }
            />
            <span className={ classes.advisorBonusSourceTextPayPal }>{ t('advisor_bonus.continue_with_pay_pal') }</span>
          </>
        );
      case 'ap':
        return (
          <>
            <span className={ classes.advisorBonusSourceTextApplePay }>
              { t('advisor_bonus.pay_with_apple_pay') }
            </span>
            <PurchaseSvgIcon
              id="purchase_button_apple_pay_icon"
              className={ classes.advisorBonusSourceImg }
            />
          </>
        );
      default:
        return '';
    }
  };

  const getPurchaseButtonTitleClassName = () => {
    if (userCredit >= selectedBonus?.amount) return classes.forMobileBuyBtnContainer_cc;
    const className = classes[`forMobileBuyBtnContainer_${ fetchData?.currentPaymentOption[0]?.kind }` as keyof typeof classes] || classes.forMobileBuyBtnContainer_cc;
    return className;
  };

  const renderFooter = () => (
    <div className={ classes.footerContainer }>
      <PurchaseButton
        purchaseAmount={ selectedBonus?.amount }
        onPurchaseComplete={ onSubmitAction }
        forMobileBuyBtnOnClick={ getforMobileBuyBtnOnClick() }
        purchaseButtonTitle={ getPurchaseButtonTitle() }
        purchaseButtonTitleClassName={ getPurchaseButtonTitleClassName() }
        purchaseButtonLoading={ skeletonLoading || purchaseButtonProcessing }
      />
      <div className={ classes.skipButton } data-testid="advisor-bonus-skip-button">
        <Button
          type="link"
          title={ t('advisor_bonus.skip') }
          onClick={ onSkipClick }
        />
      </div>
      <div className={ classes.footer } data-testid="advisor-bonus-footer">
        { t('advisor_bonus.footer_text') }
      </div>
    </div>
  );

  const renderAdvisorBonusContainer = () => (
    <>
      <div id="purchase-svg-sprite-container" style={ { position: 'absolute', top: '-1500px' } } />
      <div className={ cn(classes.container, classNames) } data-testid="advisor-bonus-container" ref={ currentRef }>
        <div className={ classes.bonusInfoContainer }>
          <div className={ classes.bonusContent }>
            { renderAdvisorAvatar() }
            { renderAdvisorTitle() }
            { renderBonusList() }
            { renderBalanceContainer() }
            { bonusSentPopoverShow && renderBonusSentPopover() }
            { Object.keys(bonusSentPopoverErroShow).length > 0 && renderBonusSentPopoverError() }
          </div>
        </div>
        { renderFooter() }
      </div>
    </>
  );

  return renderAdvisorBonusContainer();
};

export default withPGTranslation(AdvisorBonus);
